import type { ComponentChildren, JSX } from 'preact';

/**
 * 160 (FR-009): render stored legal HTML as elements, never as markup.
 *
 * The platform already sanitises on save (`backend/src/shared/htmlSanitize.ts`), so what arrives
 * here has been through an allowlist. This is the SECOND control, and the two are deliberately
 * independent: this side never hands a string to the browser as HTML, so a hole in the sanitiser is
 * not by itself enough to run script in a customer's browser.
 *
 * Parsed with `DOMParser`, which builds a detached document — script elements in it are inert, and
 * nothing in the parsed tree is attached to the live page. Only the elements below are turned into
 * output; anything else contributes its TEXT, because a legal document losing a clause over one
 * unexpected tag is its own defect.
 */

const BLOCK_ELEMENTS = new Set(['h2', 'h3', 'p', 'ul', 'ol', 'li']);
const INLINE_ELEMENTS = new Set(['strong', 'em', 'a', 'br']);

/** Elements whose CONTENT is dropped too — it is code, not prose. */
const CONTENT_DROPPED = new Set(['script', 'style']);

const SAFE_SCHEMES = ['http:', 'https:', 'mailto:'];

/** The tags rendered through the dynamic wrapper below. */
type WrapperTag = 'h2' | 'h3' | 'p' | 'ul' | 'ol' | 'li' | 'strong' | 'em';

const CLASS_NAMES: Record<string, string> = {
  h2: 'text-base font-semibold text-text mt-6 mb-2',
  h3: 'text-sm font-semibold text-text mt-4 mb-2',
  p: 'text-sm text-gray-400 leading-relaxed mb-3',
  ul: 'list-disc pl-5 mb-3 flex flex-col gap-1',
  ol: 'list-decimal pl-5 mb-3 flex flex-col gap-1',
  li: 'text-sm text-gray-400 leading-relaxed',
  a: 'text-primary underline',
};

/**
 * The same scheme rule the platform applies, restated here rather than imported because the two
 * controls must not share a defect. Normalises away the characters a browser ignores before
 * deciding, so `  JaVaScRiPt:` cannot walk past it.
 */
function safeHref(raw: string | null): string | undefined {
  if (!raw) return undefined;
  const normalised = [...raw]
    .filter((character) => (character.codePointAt(0) ?? 0) > 0x20)
    .join('')
    .toLowerCase();
  if (SAFE_SCHEMES.some((scheme) => normalised.startsWith(scheme))) return raw.trim();
  return undefined;
}

function renderNode(node: Node, key: string): ComponentChildren {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const element = node as Element;
  const tag = element.tagName.toLowerCase();

  if (CONTENT_DROPPED.has(tag)) return null;

  const children = renderChildren(element, key);

  if (tag === 'br') return <br key={key} />;

  if (tag === 'a') {
    const href = safeHref(element.getAttribute('href'));
    // A link whose destination is refused still shows its words — the sentence is the operator's.
    if (!href) return <span key={key}>{children}</span>;
    return (
      <a key={key} href={href} className={CLASS_NAMES.a} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }

  if (BLOCK_ELEMENTS.has(tag) || INLINE_ELEMENTS.has(tag)) {
    // Preact 11: the union of every intrinsic element is too deep for tsc (TS2589); `a` and `br` are handled above.
    const Tag = tag as WrapperTag;
    return (
      <Tag key={key} className={CLASS_NAMES[tag]}>
        {children}
      </Tag>
    );
  }

  // Unknown element: keep the words, drop the tag.
  return <span key={key}>{children}</span>;
}

function renderChildren(parent: Node, keyPrefix: string): ComponentChildren[] {
  return Array.from(parent.childNodes).map((child, index) =>
    renderNode(child, `${keyPrefix}-${index}`),
  );
}

export interface RichTextProps {
  html: string;
  className?: string;
}

export function RichText({ html, className = '' }: RichTextProps) {
  // `DOMParser` is available in every browser this PWA supports and in jsdom. A parse failure
  // yields an empty body rather than throwing, which is the behaviour a legal page wants: it shows
  // nothing rather than breaking the screen around it.
  const parsed = new DOMParser().parseFromString(html ?? '', 'text/html');

  return <div className={className}>{renderChildren(parsed.body, 'rt')}</div>;
}
